import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook/layout";
import {
  getNewLookTokens,
  NEW_LOOK_GAP,
  NEW_LOOK_RADIUS,
} from "common/NewLook/tokens.ts";
import { NewLookSummarySlots } from "features/bots/BotForm/Summary";
import { formPanel } from "features/bots/BotForm/NewLook/styles";
import { newLookFormScope } from "features/bots/BotForm/NewLook/formScope";
import useMeasure from "utils/hooks/useMeasure";
import {
  BOT_FORM_HELPERS_RAIL_ID,
  BOT_FORM_SUMMARY_WRAPPER_ID,
} from "testIds.ts";

interface NewLookBotFormLayoutProps {
  slots: NewLookSummarySlots;
  /** The step tabs (advanced view). */
  stepTabs?: ReactNode;
  /** Messages shown above the fields (copied / backtesting values). */
  messages?: ReactNode;
  /** The form steps. */
  steps: ReactNode;
  /** Chart / backtesting results, next to the form. */
  preview?: ReactNode;
  /** Backtesting history and public bots: a rail beside the preview on wide
   * screens, under it otherwise. */
  helpers?: ReactNode;
  /** The aside is a list of cards of its own (bulk bots lists). */
  asideHasCards?: boolean;
  /** Tablet widths: one column, the side content is hidden. */
  isNarrow?: boolean;
}

/**
 * New look (desktop): the bot form on the left — step tabs, the fields and a
 * pinned footer with the summary and the primary action — and the chart,
 * backtesting and helpers on the right. On wide screens the helpers get a
 * full-height rail of their own, so the public bots are in view on load.
 */
const NewLookBotFormLayout = ({
  slots,
  stepTabs,
  messages,
  steps,
  preview,
  helpers,
  asideHasCards,
  isNarrow,
}: NewLookBotFormLayoutProps) => {
  const { footer, aside, notice, advancedSwitch } = slots;
  const [gridRef, { width: gridWidth }] = useMeasure<HTMLDivElement>();
  const hasPreview = !isNarrow && !!preview;
  const hasRail = hasPreview && !!helpers && gridWidth >= RAIL_MIN_WIDTH;
  const hasHeader = !!advancedSwitch || !!stepTabs;

  const side = !isNarrow && (preview || helpers || aside) && (
    <Side $isChartLayout={hasPreview}>
      {hasPreview && <PreviewPanel>{preview}</PreviewPanel>}
      {helpers && !hasRail && (
        <HelpersPanel $withPreview={hasPreview}>{helpers}</HelpersPanel>
      )}
      {aside &&
        (asideHasCards ? (
          <AsideCards>{aside}</AsideCards>
        ) : (
          <AsidePanel>{aside}</AsidePanel>
        ))}
    </Side>
  );

  return (
    <Grid
      ref={gridRef}
      $isChartLayout={hasPreview}
      $hasRail={hasRail}
      $isNarrow={isNarrow}
      $hasSide={!!side}
    >
      <FormPanel $isNarrow={isNarrow}>
        {hasHeader && (
          <PanelHeader $hasTabs={!!stepTabs} $hasSwitch={!!advancedSwitch}>
            {advancedSwitch && (
              <SwitchRow $hasTabs={!!stepTabs}>{advancedSwitch}</SwitchRow>
            )}
            {stepTabs}
          </PanelHeader>
        )}
        <PanelBody $isNarrow={isNarrow}>
          <Notices>
            {messages}
            {notice}
          </Notices>
          {steps}
        </PanelBody>
        <PanelFooter
          data-testid={BOT_FORM_SUMMARY_WRAPPER_ID}
          $isNarrow={isNarrow}
        >
          {footer}
        </PanelFooter>
      </FormPanel>
      {side}
      {hasRail && (
        <RailPanel data-testid={BOT_FORM_HELPERS_RAIL_ID}>{helpers}</RailPanel>
      )}
      {isNarrow && aside && <AsidePanel>{aside}</AsidePanel>}
    </Grid>
  );
};

export default NewLookBotFormLayout;

/** Width of the form column next to the chart. */
const FORM_COLUMN = "clamp(380px, 38%, 460px)";
/** From this page width the helpers move to a rail beside the chart (the
 * chart keeps about 700px). */
const RAIL_MIN_WIDTH = 1500;
const RAIL_COLUMN = "clamp(320px, 20%, 380px)";

const Grid = styled.div<{
  $isChartLayout?: boolean;
  $hasRail?: boolean;
  $isNarrow?: boolean;
  $hasSide?: boolean;
}>`
  height: 100%;
  display: grid;
  gap: ${NEW_LOOK_GAP}px;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} 24px;
  grid-template-columns: ${(p) =>
    p.$isChartLayout
      ? `${FORM_COLUMN} minmax(0, 1fr)${p.$hasRail ? ` ${RAIL_COLUMN}` : ""}`
      : p.$hasSide
      ? "minmax(0, 1fr) clamp(320px, 34%, 400px)"
      : "minmax(0, 720px)"};
  grid-template-rows: minmax(0, 100%);
  ${(p) =>
    !p.$isChartLayout &&
    css`
      justify-content: center;
      align-items: start;
      max-width: 1180px;
      margin: 0 auto;
    `};
  ${(p) =>
    p.$isNarrow &&
    css`
      max-width: none;
      grid-template-columns: minmax(0, 640px);
      grid-template-rows: auto;
      align-content: start;
      overflow-y: auto;
      padding: 16px 20px 24px;
    `};
`;
const FormPanel = styled.section<{ $isNarrow?: boolean }>`
  ${formPanel};
  ${newLookFormScope};
  align-self: start;
  min-width: 0;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
  ${(p) =>
    p.$isNarrow &&
    css`
      max-height: none;
      overflow: visible;
    `};
`;
const PanelHeader = styled.div<{ $hasTabs?: boolean; $hasSwitch?: boolean }>`
  flex: none;
  ${(p) =>
    !p.$hasTabs &&
    css`
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
    `};
`;
/* 16px above the switch and 16px to the hairline under it (the step tabs
   bring their own 12px when they follow). */
const SwitchRow = styled.div<{ $hasTabs?: boolean }>`
  display: flex;
  padding: 16px 20px ${(p) => (p.$hasTabs ? 4 : 16)}px;
`;
const PanelBody = styled.div<{ $isNarrow?: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  ${(p) => p.theme.scrollbarStyle};
  ${(p) =>
    p.$isNarrow &&
    css`
      overflow: visible;
    `};
`;
const Notices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  &:empty {
    display: none;
  }
  > div:empty {
    display: none;
  }
`;
const PanelFooter = styled.div<{ $isNarrow?: boolean }>`
  flex: none;
  position: relative;
  /* Same space above the summary, above the actions and below them. */
  padding: 16px 20px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).panel};
  ${(p) =>
    p.$isNarrow &&
    css`
      position: sticky;
      bottom: 0;
      z-index: 5;
      border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px;
    `};
`;
const Side = styled.div<{ $isChartLayout?: boolean }>`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
  ${(p) =>
    p.$isChartLayout
      ? css`
          height: 100%;
          overflow-y: auto;
          ${p.theme.scrollbarStyle};
        `
      : css`
          max-height: 100%;
        `};
`;
const PreviewPanel = styled.div`
  ${formPanel};
  position: relative;
  flex: 1 1 0;
  min-height: 440px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const HelpersPanel = styled.div<{ $withPreview?: boolean }>`
  ${formPanel};
  position: relative;
  flex: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: ${(p) => (p.$withPreview ? "clamp(250px, 36%, 330px)" : "100%")};
`;
const RailPanel = styled.div`
  ${formPanel};
  position: relative;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const AsidePanel = styled.div`
  ${formPanel};
  position: relative;
  min-height: 0;
  overflow-y: auto;
  ${(p) => p.theme.scrollbarStyle};
`;
const AsideCards = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 4px;
  > * {
    margin-top: 0 !important;
  }
  ${(p) => p.theme.scrollbarStyle};
`;
